// Framework Universe — CMS-ready. Descriptions are intentionally hedged:
// applicability depends on organisation type, jurisdiction and reporting period.
export const frameworks = [
  {
    code: "BRSR", name: "Business Responsibility and Sustainability Reporting", family: "Indian disclosure",
    what: "India's sustainability disclosure format for listed entities, covering environmental, social and governance performance across a defined set of indicators.",
    who: "Listed companies in India, per applicability criteria set out by the securities regulator; voluntarily adopted by some others.",
    why: "It is the primary structured ESG disclosure for India's listed market and increasingly shapes how Indian companies collect ESG data.",
    help: "Readiness assessment, data collection, gap assessment, drafting, evidence documentation and BRSR Core preparation.",
  },
  {
    code: "BRSR Core", name: "BRSR Core (assurance-oriented subset)", family: "Indian disclosure",
    what: "A defined subset of BRSR indicators for which value-chain and assurance-related expectations apply progressively for eligible listed entities.",
    who: "Listed entities within the applicability glide path defined by the securities regulator.",
    why: "It raises the bar on data quality and traceability, making internal controls and evidence management essential.",
    help: "Evidence documentation, data controls, value-chain data planning and assurance readiness.",
  },
  {
    code: "GRI", name: "Global Reporting Initiative", family: "Global reporting",
    what: "A widely used global standard for sustainability reporting, built around impact materiality and modular topic standards.",
    who: "Organisations of any size and sector, worldwide.",
    why: "GRI-aligned reporting is a common expectation in stakeholder communications and supports comparable disclosure.",
    help: "Framework selection, materiality, indicator mapping and report preparation.",
  },
  {
    code: "ISSB", name: "International Sustainability Standards Board", family: "Global reporting",
    what: "The board developing the global baseline for investor-focused sustainability disclosure — IFRS S1 and IFRS S2.",
    who: "Relevant to entities reporting to capital markets, as adopted into local regimes over time.",
    why: "The ISSB baseline is shaping how jurisdictions converge sustainability disclosure for investors.",
    help: "Adoption readiness, gap assessment and disclosure architecture.",
  },
  {
    code: "IFRS S1", name: "IFRS S1 — General Sustainability Disclosures", family: "Global reporting",
    what: "Sets out requirements for disclosing sustainability-related financial information relevant to investor decisions.",
    who: "Entities within jurisdictions adopting the ISSB baseline.",
    why: "It defines the general sustainability disclosure layer that climate and future themes build on.",
    help: "Gap assessment, data mapping and disclosure drafting support.",
  },
  {
    code: "IFRS S2", name: "IFRS S2 — Climate-related Disclosures", family: "Climate disclosure",
    what: "Specifies climate-related disclosure requirements — governance, strategy, risk management, metrics and targets — building on TCFD architecture.",
    who: "Entities within jurisdictions adopting the ISSB baseline.",
    why: "Climate disclosure is converging on this baseline; readiness protects against future rework.",
    help: "Climate risk analysis, metrics and targets, scenario analysis support.",
  },
  {
    code: "TCFD", name: "Task Force on Climate-related Financial Disclosures", family: "Climate disclosure",
    what: "The four-pillar framework (governance, strategy, risk management, metrics & targets) that shaped modern climate disclosure.",
    who: "Companies and financial institutions; embedded into several regulatory regimes.",
    why: "Its structure remains the common language of climate risk disclosure worldwide.",
    help: "TCFD-aligned climate risk assessment, scenario analysis and disclosure drafting.",
  },
  {
    code: "TNFD", name: "Taskforce on Nature-related Financial Disclosures", family: "Nature",
    what: "A risk-management and disclosure framework for nature-related dependencies, impacts, risks and opportunities.",
    who: "Organisations with material nature exposure; voluntary adoption phase.",
    why: "Nature-related risk is the emerging frontier of ESG expectation after climate.",
    help: "TNFD-aligned biodiversity assessment, dependency mapping and disclosure readiness.",
  },
  {
    code: "GHG Protocol", name: "GHG Protocol", family: "Carbon accounting",
    what: "The most widely used methodology for accounting and reporting greenhouse gas emissions across Scope 1, 2 and 3.",
    who: "Any organisation measuring its carbon footprint.",
    why: "Almost every carbon disclosure and target references GHG Protocol-aligned accounting.",
    help: "Inventory design, boundary setting, data collection and factor assessment.",
  },
  {
    code: "ISO 14064", name: "ISO 14064", family: "Carbon accounting",
    what: "An international standard family for quantifying, monitoring and reporting GHG emissions and reductions.",
    who: "Organisations seeking standards-based GHG quantification and verification readiness.",
    why: "It provides a rigorous, auditable structure that supports assurance conversations.",
    help: "ISO 14064-aligned inventory design and documentation.",
  },
  {
    code: "UN SDGs", name: "UN Sustainable Development Goals", family: "Global goals",
    what: "The global goals for sustainable development, used by many organisations to frame contribution and impact.",
    who: "Any organisation connecting strategy to sustainable development outcomes.",
    why: "The SDGs give a shared vocabulary for impact — when used with honesty about contribution.",
    help: "SDG mapping, impact framing and outcome indicators.",
  },
  {
    code: "UN PRI", name: "UN Principles for Responsible Investment", family: "Sustainable finance",
    what: "A global proponent network for responsible investment, with principles covering ESG integration, stewardship and reporting.",
    who: "Investors and investment managers who become signatories.",
    why: "Signatory expectations shape how funds structure ESG policy and reporting.",
    help: "Responsible Investment Policy, integration design and PRI-aligned reporting support.",
  },
  {
    code: "SFDR", name: "Sustainable Finance Disclosure Regulation", family: "Sustainable finance",
    what: "An EU regulation governing sustainability-related disclosure by financial market participants, including PAI and product-level requirements.",
    who: "Financial market participants with EU nexus; Indian funds with EU distribution may encounter it.",
    why: "Its data demands drive portfolio ESG data collection even outside the EU.",
    help: "SFDR support, PAI assessment, DNSH assessment and data planning.",
  },
  {
    code: "EU Taxonomy", name: "EU Taxonomy", family: "Sustainable finance",
    what: "A classification system defining environmentally sustainable economic activities for EU framework purposes.",
    who: "Entities within SFDR and related EU disclosure scopes.",
    why: "It underpins what counts as 'sustainable' for EU-facing sustainable finance products.",
    help: "Activity assessment and substantial-contribution / DNSH-oriented support.",
  },
  {
    code: "SASB", name: "Sustainability Accounting Standards Board", family: "Global reporting",
    what: "Industry-specific standards identifying sustainability issues reasonably likely to affect financial condition, now maintained under the ISSB.",
    who: "Companies reporting to investors, often alongside other frameworks.",
    why: "Its industry lens keeps disclosure focused on financially relevant topics.",
    help: "SASB-aligned metric selection and reporting support.",
  },
];

export const frameworkDisclaimer =
  "Framework applicability depends on organisation type, jurisdiction, reporting period and regulatory requirements. SarvaRitam provides advisory support and does not constitute legal advice.";
